import React, { useEffect, useRef, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { OutletMenuSearch } from '@src/components/SiteHeader/SearchBar/OutletMenuSearch/OutletMenuSearch'
import { breakpoints } from '@src/constants/breakpoints'
import { screenResolutionVar } from '@src/models/screenResolution'
import { CaretLeft } from '@src/components/SVGS/CaretLeft'
import { convertToSlug } from '@src/utils/convertToSlug'
import { useMarketplace } from '@src/hooks/useMarketplace'

import {
  Capsule,
  CapsuleContainer,
  Container,
  FilterOverlay,
  OutletMenuSearchContainer,
} from './OutletMenuSection.styles'

import { OutletMenuItemGroupTree } from '../menuItemGroupTreeType'
import { useOutletPageContext } from '../OutletPageContext'
import {
  BackToOutletContainer,
  BackToOutletTitle,
} from '../OutletStackedMenu/OutletStackedMenu.styles'
import { useCheckoutRouter } from '@src/hooks/useCheckoutRouter/useCheckoutRouter'

export const OutletMenuSection: React.FC<{
  parentMenus: Pick<
    OutletMenuItemGroupTree[number]['parentMenu'],
    'id' | 'name'
  >[]
  activeMenuId: string | null
  outletId: string
  outletDisplayName: string
  isInStackedMenuView: boolean
}> = ({
  parentMenus,
  activeMenuId,
  outletId,
  outletDisplayName,
  isInStackedMenuView,
}) => {
  const {
    setActiveMenuId,
    setManualNavigation,
    showMenuSearchResults,
    isStackedMenu,
  } = useOutletPageContext()
  const { width } = useReactiveVar(screenResolutionVar)
  const navigate = useNavigate()
  const { urlPath } = useMarketplace()
  const { t } = useTranslation('outletOverview')
  const { displayPermanentCheckout } = useCheckoutRouter()

  const [showSearchBar, setShowSearchBar] = useState(isStackedMenu)

  const containerRef = useRef<HTMLDivElement>(null)
  const activeElementRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (activeMenuId) {
      const element = activeElementRef.current
      if (containerRef.current && element) {
        const containerBounds = containerRef.current.getBoundingClientRect()
        const activeElPosition = element.getBoundingClientRect()

        const isOffscreen =
          activeElPosition.left < containerBounds.left ||
          activeElPosition.right > containerBounds.right

        if (isOffscreen)
          requestAnimationFrame(() => {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            })
          })
      }
    }
  }, [activeMenuId])

  const scrollToElement = (elementId: string) => {
    const element = document.getElementById(elementId)
    if (!element) return

    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - 200

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })
  }

  const handleCapsuleClick = (parentMenuId: string) => {
    setManualNavigation(true)
    setActiveMenuId(parentMenuId)
    scrollToElement(parentMenuId)
  }

  return (
    <>
      {width < breakpoints.wideDesktop && <FilterOverlay />}
      <Container $isStackedMenuView={isStackedMenu && isInStackedMenuView}>
        {!showSearchBar && !isStackedMenu && (
          <CapsuleContainer ref={containerRef}>
            {!showMenuSearchResults &&
              parentMenus.map(parentMenu => (
                <Capsule
                  onClick={() => handleCapsuleClick(parentMenu.id)}
                  active={activeMenuId === parentMenu.id}
                  key={parentMenu.id}
                  ref={el => {
                    if (activeMenuId === parentMenu.id) {
                      activeElementRef.current = el
                    }
                  }}
                >
                  {parentMenu.name}
                </Capsule>
              ))}
          </CapsuleContainer>
        )}
        {isInStackedMenuView && (
          <BackToOutletContainer
            onClick={() =>
              navigate(
                `/${urlPath}/${outletId}/${convertToSlug(
                  outletDisplayName
                )}/menu`,
                { replace: true }
              )
            }
          >
            <CaretLeft id="caretLeft" />
            <BackToOutletTitle>
              {t('back_to_outlet_page', {
                outletName: outletDisplayName,
              })}
            </BackToOutletTitle>
          </BackToOutletContainer>
        )}
        <OutletMenuSearchContainer
          $isStackedMenuView={isStackedMenu && isInStackedMenuView}
          displayPermanentCheckout={displayPermanentCheckout}
        >
          <OutletMenuSearch
            outletId={outletId}
            showSearchBar={showSearchBar}
            setShowSearchBar={setShowSearchBar}
          />
        </OutletMenuSearchContainer>
      </Container>
    </>
  )
}
